/**
 * Money formatting for the Live Impact panel (SE-7002, FC-1.1).
 *
 * The panel renders money in millions to one decimal, matching the Figma
 * frames exactly: a signed delta reads `+ €10.4M`, an absolute figure reads
 * `€ 80.0M`. The space after the sign and after the symbol is the design's,
 * not an accident — it is what keeps a column of deltas aligned on the digit
 * rather than on the glyph.
 *
 * Zero is rendered unsigned. `+ €0.0M` claims a movement that did not happen.
 * @module features/financial-compliance/domain/formatImpact
 */

/** Currency units per million. */
const ONE_MILLION = 1_000_000;

/** Below this, a value rounds to €0.0M and is treated as no movement. */
const ZERO_EPSILON = ONE_MILLION / 20;

/**
 * Millions, to one decimal place.
 * @param value - Amount in currency units.
 * @returns The magnitude in millions.
 */
const millions = (value: number): string =>
  (Math.abs(value) / ONE_MILLION).toFixed(1);

/**
 * Format an absolute money figure, e.g. `€ 80.0M`.
 * @param value - Amount in currency units.
 * @returns The formatted figure.
 */
export const formatMillions = (value: number): string =>
  `€ ${millions(value)}M`;

/**
 * Format a signed delta, e.g. `+ €10.4M` / `- €10.4M` / `€ 0.0M`.
 * @param value - Change in currency units.
 * @returns The formatted delta.
 */
export const formatSignedMillions = (value: number): string => {
  if (!Number.isFinite(value) || Math.abs(value) < ZERO_EPSILON) {
    return `€ ${(0).toFixed(1)}M`;
  }
  return `${value > 0 ? "+" : "-"} €${millions(value)}M`;
};

/**
 * Format a percentage to one decimal, e.g. `88.4%`.
 * @param ratio - A ratio where 1 means 100%.
 * @returns The formatted percentage.
 */
export const formatPercent = (ratio: number): string =>
  `${(Number.isFinite(ratio) ? ratio * 100 : 0).toFixed(1)}%`;

/**
 * Format a whole-number percentage cap, e.g. `70%`.
 * @param ratio - A ratio where 1 means 100%.
 * @returns The formatted cap.
 */
export const formatPercentCap = (ratio: number): string =>
  `${Math.round((Number.isFinite(ratio) ? ratio : 0) * 100)}%`;

/**
 * Format a money magnitude with no decimal, e.g. `€21M`.
 *
 * Used inside headline sentences, where the design writes the amount tight
 * against the symbol and without a decimal — "€21M over the wage budget".
 * @param value - Amount in currency units.
 * @returns The formatted magnitude.
 */
export const formatMillionsTight = (value: number): string =>
  `€${Math.round(Math.abs(value) / ONE_MILLION)}M`;
